import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { XCircle, AlertTriangle, Home, RefreshCw, Stethoscope } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function AuthErrorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorType = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');
  const { t } = useTranslation('doctor-platform');

  const getErrorDetails = () => {
    switch (errorType) {
      case 'access_denied':
        return {
          title: t('auth.authError.cases.accessDenied.title'),
          description: errorDescription || t('auth.authError.cases.accessDenied.description'),
          icon: XCircle,
          iconColor: 'text-red-600 dark:text-red-400'
        };
      case 'server_error':
        return {
          title: t('auth.authError.cases.serverError.title'),
          description: errorDescription || t('auth.authError.cases.serverError.description'),
          icon: AlertTriangle,
          iconColor: 'text-yellow-600 dark:text-yellow-400'
        };
      case 'invalid_request':
        return {
          title: t('auth.authError.cases.invalidRequest.title'),
          description: errorDescription || t('auth.authError.cases.invalidRequest.description'),
          icon: XCircle,
          iconColor: 'text-red-600 dark:text-red-400'
        };
      case 'unauthorized_client':
        return {
          title: t('auth.authError.cases.unauthorized.title'),
          description: t('auth.authError.cases.unauthorized.description'),
          icon: XCircle,
          iconColor: 'text-red-600 dark:text-red-400'
        };
      case 'invalid_token':
        return {
          title: t('auth.authError.cases.invalidToken.title'),
          description: t('auth.authError.cases.invalidToken.description'),
          icon: AlertTriangle,
          iconColor: 'text-yellow-600 dark:text-yellow-400'
        };
      case 'email_not_confirmed':
        return {
          title: t('auth.authError.cases.emailNotConfirmed.title'),
          description: t('auth.authError.cases.emailNotConfirmed.description'),
          icon: AlertTriangle,
          iconColor: 'text-yellow-600 dark:text-yellow-400'
        };
      default:
        return {
          title: t('auth.authError.cases.default.title'),
          description: errorDescription || t('auth.authError.cases.default.description'),
          icon: AlertTriangle,
          iconColor: 'text-yellow-600 dark:text-yellow-400'
        };
    }
  };

  const errorDetails = getErrorDetails();
  const IconComponent = errorDetails.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="h-16 w-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <IconComponent className={`h-8 w-8 ${errorDetails.iconColor}`} />
            </div>
          </div>
          <CardTitle className="text-2xl">{errorDetails.title}</CardTitle>
          <CardDescription className="mt-2">
            {errorDetails.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Medical Portal Branding */}
          <div className="flex justify-center pb-2">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Stethoscope className="h-5 w-5" />
              <span className="text-sm font-medium">{t('auth.authError.portalLabel')}</span>
            </div>
          </div>

          {/* Error Code Display */}
          {errorType && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-muted-foreground text-center">
                {t('auth.authError.errorCode', { code: errorType })}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/auth/login')}
              className="w-full"
            >
              <Home className="mr-2 h-4 w-4" />
              {t('auth.authError.backToLogin')}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="w-full"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('auth.authError.tryAgain')}
            </Button>
          </div>

          {/* Help Text */}
          <div className="text-center text-sm text-muted-foreground pt-4">
            <p>
              {t('auth.authError.help')}{' '}
              <a href="mailto:support@example.com" className="text-primary hover:underline">
                support@example.com
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
